import React, { useContext, useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { debounce } from 'lodash';
import { useAuth } from '../../AuthContext';
import useApi from '../../Api';
import { toast } from 'react-toastify';
import { validateTextInput } from '../common/TextValidator';
import QuestionnaireForm from '../questionnaire/QuestionnaireForm';
import QuestionnaireContext from '../questionnaire/QuestionnaireContext';
import questions from '../questionnaire/questions';
import Popup from '../common/popup/Popup';
import Button from '@mui/material/Button';

const ProfileTravelPreferences = forwardRef(({ onClose, isMobile }, ref) => {
  const { answers, setAnswers } = useContext(QuestionnaireContext);
  const { user, token } = useAuth();
  const apiClient = useApi();

  const [dateRange, setDateRange] = useState([null, null]);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [customOptions, setCustomOptions] = useState({});
  const [addingCustomOption, setAddingCustomOption] = useState(null);
  const [customOptionInput, setCustomOptionInput] = useState('');
  const [validationError, setValidationError] = useState('');
  const [showCustomPrompt, setShowCustomPrompt] = useState(false);
  const [customPromptInput, setCustomPromptInput] = useState('');
  const [customPromptError, setCustomPromptError] = useState('');

  const startDate = dateRange[0];
  const endDate = dateRange[1];

  const [baselineAnswers, setBaselineAnswers] = useState('');
  const [baselineDates, setBaselineDates] = useState('');
  const [baselinePrompt, setBaselinePrompt] = useState('');

  const [initialized, setInitialized] = useState(false);
  const [unsavedPopupVisible, setUnsavedPopupVisible] = useState(false);

  const handleDateRangeChange = (update) => {
    setDateRange(update);
  };

  const isAtLeastOneAnswered = () => {
    return (
      Object.keys(answers).length > 0 ||
      (startDate && endDate) ||
      customPromptInput.trim().length > 0
    );
  };

  const handleToggleCustomPrompt = () => {
    setShowCustomPrompt((prev) => !prev);
    setCustomPromptError('');
  };

  const handleCustomPromptChange = (e) => {
    setCustomPromptInput(e.target.value);
    if (customPromptError) {
      const { isValid } = validateTextInput(e.target.value, {
        maxLength: 500,
        allowSpecialChars: true,
      });
      if (isValid) {
        setCustomPromptError('');
      }
    }
  };

  const handlePromptValidationError = (val) => {
    setValidationError(val);
  };

  const handleStartAddingOption = (id) => {
    setAddingCustomOption(id);
    setCustomOptionInput('');
    setValidationError('');
  };

  const handleCustomOptionInputChange = (e) => {
    setCustomOptionInput(e.target.value);
  };

  const onUpdateAnswers = (questionId, option, type) => {
    if (type === 'multi-select') {
      const currentSelections = answers[questionId] || [];
      const updated = currentSelections.includes(option)
        ? currentSelections.filter((item) => item !== option)
        : [...currentSelections, option];
      setAnswers((prev) => ({ ...prev, [questionId]: updated }));
    } else {
      setAnswers((prev) => ({
        ...prev,
        [questionId]: option === answers[questionId] ? null : option
      }));
    }
  };

  const onAddCustomOption = (questionId) => {
    const trimmedInput = customOptionInput.trim();
    if (!trimmedInput) {
      setValidationError('Custom option cannot be empty.');
      return;
    }
    const { isValid, errors, sanitizedText } = validateTextInput(trimmedInput, {
      maxLength: 25,
      allowSpecialChars: true,
    });
    if (!isValid) {
      setValidationError(errors.join(' '));
      return;
    }
    const questionObj = questions.find((q) => q.id === questionId);
    if (questionObj?.type === 'multi-select') {
      setAnswers((prev) => {
        const oldVal = prev[questionId];
        if (Array.isArray(oldVal)) {
          return { ...prev, [questionId]: [...oldVal, sanitizedText] };
        } else if (oldVal) {
          return { ...prev, [questionId]: [oldVal, sanitizedText] };
        } else {
          return { ...prev, [questionId]: [sanitizedText] };
        }
      });
    } else {
      setAnswers((prev) => ({
        ...prev,
        [questionId]: sanitizedText
      }));
    }
    setCustomOptions((prev) => {
      const existing = prev[questionId] || [];
      if (existing.length >= 5) {
        setValidationError('You can add up to 5 custom options per question.');
        return prev;
      }
      const isDuplicate = existing.some(
        (opt) => opt.toLowerCase() === sanitizedText.toLowerCase()
      );
      if (isDuplicate) {
        setValidationError('This custom option already exists.');
        return prev;
      }
      return {
        ...prev,
        [questionId]: [...existing, sanitizedText]
      };
    });
    setAddingCustomOption(null);
    setCustomOptionInput('');
    setValidationError('');
  };

  const onDeleteCustomOption = (questionId, option) => {
    setCustomOptions((prev) => {
      const existing = prev[questionId] || [];
      return {
        ...prev,
        [questionId]: existing.filter((opt) => opt !== option),
      };
    });
    if (answers[questionId]) {
      if (Array.isArray(answers[questionId])) {
        setAnswers((prev) => ({
          ...prev,
          [questionId]: prev[questionId].filter((opt) => opt !== option),
        }));
      } else if (answers[questionId] === option) {
        setAnswers((prev) => ({
          ...prev,
          [questionId]: null,
        }));
      }
    }
  };

  const handleReset = () => {
    setAnswers({});
    setDateRange([null, null]);
    setErrorMessage('');
    setCustomOptions({});
    setAddingCustomOption(null);
    setCustomOptionInput('');
    setValidationError('');
    setShowCustomPrompt(false);
    setCustomPromptInput('');
    setCustomPromptError('');
  };

  const removeUnselectedSingleSelectCustoms = (updatedAnswers) => {
    const pruned = { ...customOptions };
    questions.forEach((q) => {
      if (q.type === 'single-select') {
        const currentAnswer = updatedAnswers[q.id];
        if (Array.isArray(pruned[q.id])) {
          pruned[q.id] = pruned[q.id].filter((opt) => opt === currentAnswer);
        }
      }
    });
    setCustomOptions(pruned);
  };

  const handleSubmitDebounced = debounce(async (prefs) => {
    if (!user || !token) return;
    setLoading(true);
    try {
      await apiClient.put('/explore/user_preferences', prefs, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      toast.success('Preferences saved successfully.', {
        position: "top-center",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      setAnswers(prefs);
      removeUnselectedSingleSelectCustoms(prefs);
      setBaselineAnswers(JSON.stringify(prefs));
      setBaselineDates(JSON.stringify([startDate, endDate]));
      setBaselinePrompt(prefs.custom_prompt || '');
    } catch (err) {
      toast.error('Failed to save preferences. Please try again.', {
        position: "top-center",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } finally {
      setLoading(false);
      if (onClose) {
        onClose();
      }
    }
  }, 500);

  const onSubmitSavePrefs = (e) => {
    if (e) e.preventDefault();
    let trimmedPrompt = customPromptInput.trim();
    let sanitizedCustomPrompt = '';
    if (trimmedPrompt.length > 0) {
      const { isValid, errors, sanitizedText } = validateTextInput(trimmedPrompt, {
        maxLength: 500,
        allowSpecialChars: true,
      });
      if (!isValid) {
        setCustomPromptError(errors.join(' '));
        return;
      }
      sanitizedCustomPrompt = sanitizedText;
    }
    const newPrefs = {
      ...answers,
      custom_prompt: sanitizedCustomPrompt,
      travelDates: {
        startDate: startDate ? startDate.toISOString() : null,
        endDate: endDate ? endDate.toISOString() : null,
      },
    };
    handleSubmitDebounced(newPrefs);
  };

  useEffect(() => {
    setCustomOptions((prev) => {
      const merged = { ...prev };
      questions.forEach((q) => {
        if (q.type === 'multi-select' || q.type === 'single-select') {
          const standardSet = new Set(q.options.map((option) => option.toLowerCase()));
          const userAnswer = answers[q.id];
          let derived = [];
          if (Array.isArray(userAnswer)) {
            derived = userAnswer.filter((item) => !standardSet.has(item.toLowerCase()));
          } else if (
            typeof userAnswer === 'string' &&
            userAnswer.trim() &&
            !standardSet.has(userAnswer.toLowerCase())
          ) {
            derived = [userAnswer];
          }
          const oldVals = merged[q.id] || [];
          const unionSet = new Set([...oldVals, ...derived]);
          merged[q.id] = [...unionSet];
        }
      });
      return merged;
    });
  }, [answers]);

  useEffect(() => {
    if (!initialized && user && token) {
      const loadedStart = answers.travelDates?.startDate
        ? new Date(answers.travelDates.startDate)
        : null;
      const loadedEnd = answers.travelDates?.endDate
        ? new Date(answers.travelDates.endDate)
        : null;
      setDateRange([loadedStart, loadedEnd]);
      setCustomPromptInput(answers.custom_prompt || '');
      setBaselineAnswers(JSON.stringify(answers));
      setBaselineDates(JSON.stringify([loadedStart, loadedEnd]));
      setBaselinePrompt(answers.custom_prompt || '');
      setInitialized(true);
    }
  }, [answers, initialized, user, token]);

  const localHasUnsavedChanges = () => {
    const currentAnswers = JSON.stringify(answers);
    const currentDates = JSON.stringify(dateRange);
    const currentPrompt = customPromptInput;
    return currentAnswers !== baselineAnswers ||
           currentDates !== baselineDates ||
           currentPrompt !== baselinePrompt;
  };

  const localHandleClose = () => {
    if (localHasUnsavedChanges()) {
      setUnsavedPopupVisible(true);
    } else {
      onClose();
    }
  };

  const handleUnsavedConfirm = () => {
    onSubmitSavePrefs();
    setUnsavedPopupVisible(false);
  };

  const handleUnsavedCancel = () => {
    setUnsavedPopupVisible(false);
    onClose();
  };

  useImperativeHandle(ref, () => ({
    hasUnsavedChanges: () => {
      const currentAnswers = JSON.stringify(answers);
      const currentDates = JSON.stringify(dateRange);
      const currentPrompt = customPromptInput;
      return currentAnswers !== baselineAnswers ||
             currentDates !== baselineDates ||
             currentPrompt !== baselinePrompt;
    },
    handleSave: () => {
      onSubmitSavePrefs();
    },
    handleClose: localHandleClose
  }));

  return (
    <div className="questionnaire-wrapper">
      <QuestionnaireForm
        mode="PROFILE"
        onSubmit={onSubmitSavePrefs}
        onSecondaryAction={onClose}
        onReset={handleReset}
        onAddCustomOption={onAddCustomOption}
        onDeleteCustomOption={onDeleteCustomOption}
        onUpdateAnswers={onUpdateAnswers}
        canSubmit={isAtLeastOneAnswered()}
        loading={loading}
        errorMessage={errorMessage}
        answers={answers}
        customOptions={customOptions}
        addingCustomOption={addingCustomOption}
        customOptionInput={customOptionInput}
        validationError={validationError}
        customPromptInput={customPromptInput}
        showCustomPrompt={showCustomPrompt}
        customPromptError={customPromptError}
        startDate={startDate}
        endDate={endDate}
        isMobile={isMobile}
        handleCustomPromptChange={handleCustomPromptChange}
        handleToggleCustomPrompt={handleToggleCustomPrompt}
        handleStartAddingOption={handleStartAddingOption}
        handleCustomOptionInputChange={handleCustomOptionInputChange}
        handleDateRangeChange={handleDateRangeChange}
        handlePromptValidationError={handlePromptValidationError}
        onClose={localHandleClose}
      />
      {unsavedPopupVisible && (
        <Popup
          title="Unsaved Changes"
          message="You have unsaved changes. Click Yes to save or No to discard."
          onConfirm={handleUnsavedConfirm}
          onCancel={handleUnsavedCancel}
          className="unsaved-popup"
        />
      )}
    </div>
  );
});

export default ProfileTravelPreferences;
